import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Space, Tag, Typography, Button, Input, Divider, Skeleton, Empty,
    Flex, Avatar, Image, Popconfirm, message, Pagination, Grid, Select, theme, Segmented
} from 'antd';
import {
    DeleteOutlined,
    SendOutlined,
    UserOutlined,
    RobotOutlined,
    CommentOutlined,
    TagOutlined,
    FlagOutlined,
    AppstoreOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    LockOutlined,
    MessageOutlined,
    LinkOutlined,
    HighlightOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { resolveBackendMessage } from '../../../Utils/apiMessages';
import {
    getTicket,
    updateTicket,
    deleteTicket,
    getTicketComments,
    createTicketComment,
    deleteTicketComment,
    resolveTicket,
    suggestTicketResponse
} from '../TicketServices';
import { getMessages } from '../../Messages/MessagesServices';
import MessageBubble from '../../Messages/Messages/MessageContent/MessageList/MessageBubble/MessageBubble';
import { formatFullTime } from '../../Messages/utils/messageRenderUtils';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import BeautifyControl from '../../Shared/BeautifyControl/BeautifyControl';
import ContactsServices from '../../Contacts/ContactsServices';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useVisualHighlight from '../../../Hooks/useVisualHighlight';
import { useBeautifyResetSignal } from '../../../Hooks/useBeautifyResetSignal';
import { Resource, Action } from '../../../Constants/socket';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

const STATUS_COLORS = {
    open: 'blue',
    in_progress: 'orange',
    resolved: 'green',
    closed: 'default',
};

const STATUS_DOT_COLORS = {
    open: '#1677ff',
    in_progress: '#fa8c16',
    resolved: '#52c41a',
    closed: '#8c8c8c',
};

const PRIORITY_COLORS = {
    low: 'default',
    medium: 'blue',
    high: 'orange',
    urgent: 'red',
};

const CATEGORY_COLORS = {
    escalation: 'volcano',
    reclamation: 'magenta',
    suggestion: 'cyan',
    order: 'geekblue',
    other: 'default',
};



/**
 * A single metadata row for the sidebar.
 */
const MetaRow = ({ icon, label, children }) => (
    <Flex align="flex-start" gap={10} style={{ fontSize: 13, lineHeight: '22px' }}>
        <span style={{ opacity: 0.45, width: 16, flexShrink: 0, textAlign: 'center', paddingTop: 2 }}>{icon}</span>
        <Text type="secondary" style={{ minWidth: 80, flexShrink: 0, fontSize: 12 }}>{label}</Text>
        <div style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{children}</div>
    </Flex>
);

const TicketDetail = ({ ticketId, onClose }) => {
    const [isContactPhotoPreviewOpen, setIsContactPhotoPreviewOpen] = useState(false);
    const id = ticketId;
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { isDarkMode } = useTheme();
    const { setLoading } = useGlobal();
    const { handleError } = useErrorHandler();
    const screens = useBreakpoint();
    const { token } = theme.useToken();
    const formatTicketDate = (date) => formatFullTime(date, i18n?.language) || date;

    const [ticket, setTicket] = useState(null);
    const [loading, setLocalLoading] = useState(true);
    const [comments, setComments] = useState([]);
    const [commentsMeta, setCommentsMeta] = useState(null);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentPage, setCommentPage] = useState(1);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [statusChanging, setStatusChanging] = useState(false);
    const [resolveText, setResolveText] = useState('');
    const [resolving, setResolving] = useState(false);
    const [mobileTab, setMobileTab] = useState('info');
    const {
        resetSignal: resolveBeautifyResetSignal,
        resetBeautifyControl: resetResolveBeautifyControl
    } = useBeautifyResetSignal();

    const [contextMessages, setContextMessages] = useState([]);
    const [loadingContext, setLoadingContext] = useState(false);
    const messageRefs = useRef({});
    const messageListRef = useRef(null);
    const targetMessageRef = useRef(null);
    const commentsRef = useRef([]);

    // Resizable divider state
    const [sidebarWidth, setSidebarWidth] = useState(340);
    const [isResizing, setIsResizing] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        commentsRef.current = comments;
    }, [comments]);

    useEffect(() => {
        resetResolveBeautifyControl();
    }, [id, resetResolveBeautifyControl]);

    // Visual highlights for real-time socket events
    const { highlightRow, getHighlightClass } = useVisualHighlight();

    // Subscribe to this specific ticket ID for real-time UPDATE/DELETE events
    const ticketIdArray = useMemo(() => id ? [id] : null, [id]);
    useSocketSubscription(Resource.TICKET, ticketIdArray);

    // Subscribe to loaded comment IDs for specific UPDATE events
    const commentIdsArray = useMemo(() => commentsRef.current.map(c => c.id).filter(Boolean), [commentsRef.current]);
    useSocketSubscription(Resource.TICKET_COMMENT, commentIdsArray.length > 0 ? commentIdsArray : null);

    // Merge real-time updates into local ticket state
    useSocketEvent(Resource.TICKET, Action.UPDATE, useCallback((data) => {
        if (data?.id === id) {
            setTicket(prev => prev ? { ...prev, ...data } : prev);
        }
    }, [id]));

    // Handle real-time deletion — close the detail view
    useSocketEvent(Resource.TICKET, Action.DELETE, useCallback((data) => {
        if (data?.id === id) {
            onClose();
        }
    }, [id, onClose]));


    useEffect(() => {
        const fetchContext = async () => {
            if (!ticket?.source_message_id || !ticket?.contact_id) return;
            setLoadingContext(true);
            try {
                const conversationResponse = await ContactsServices.getContactConversation(ticket.contact_id);
                const conversationId = conversationResponse?.data?.id;

                if (!conversationId) return;

                const messageId = ticket.source_message_id;
                const contextSize = 10;

                const [olderResponse, newerResponse] = await Promise.all([
                    getMessages({ conversation_id: conversationId, after: messageId, size: contextSize }),
                    getMessages({ conversation_id: conversationId, until: messageId, size: contextSize })
                ]);

                const newerMessages = newerResponse?.data || [];
                const olderMessages = olderResponse?.data || [];

                const combined = [...newerMessages, ...olderMessages];
                const unique = Array.from(new Map(combined.map(m => [m.id, m])).values());
                unique.sort((a, b) => {
                    const strA = a.status?.sent_at || '';
                    const strB = b.status?.sent_at || '';
                    return strA.localeCompare(strB);
                });

                setContextMessages(unique);
            } catch (error) {
                // Non-critical: context section simply won't render
            } finally {
                setLoadingContext(false);
            }
        };

        if (ticket?.source_message_id) {
            fetchContext();
        }
    }, [ticket?.source_message_id, ticket?.contact_id]);

    useEffect(() => {
        if (contextMessages.length > 0 && ticket?.source_message_id && messageListRef.current) {
            const targetEl = targetMessageRef.current;
            const container = messageListRef.current;
            if (targetEl && container) {
                setTimeout(() => {
                    const containerRect = container.getBoundingClientRect();
                    const targetRect = targetEl.getBoundingClientRect();

                    const relativeTop = targetRect.top - containerRect.top;
                    const currentScrollTop = container.scrollTop;
                    const centerScroll = currentScrollTop + relativeTop - (container.clientHeight / 2) + (targetRect.height / 2);

                    container.scrollTo({ top: centerScroll, behavior: 'smooth' });
                }, 150);
            }
        }
    }, [contextMessages, ticket?.source_message_id]);

    const fetchTicket = useCallback(async () => {
        try {
            setLocalLoading(true);
            const { data } = await getTicket(id);
            setTicket(data);
        } catch (error) {
            handleError(error);
        } finally {
            setLocalLoading(false);
        }
    }, [id, handleError]);

    const fetchComments = useCallback(async (page = 1) => {
        try {
            setCommentsLoading(true);
            const { data, metadata } = await getTicketComments({
                ticketId: id,
                page,
                size: 25,
                sort_by: 'created_at',
                sort_direction: 'asc',
            });
            setComments(data || []);
            setCommentsMeta(metadata);
        } catch (error) {
            handleError(error);
        } finally {
            setCommentsLoading(false);
        }
    }, [id, handleError]);

    useEffect(() => {
        fetchTicket();
        fetchComments(1);
    }, [fetchTicket, fetchComments]);

    // Handle real-time comment additions
    useSocketEvent(Resource.TICKET_COMMENT, Action.CREATE, useCallback((data) => {
        if (data?.ticket_id === id) {
            fetchComments(Math.max(5, commentPage));
        }
        if (data?.id) highlightRow(data.id);
    }, [id, commentPage, fetchComments, highlightRow]));

    // Handle real-time comment updates
    useSocketEvent(Resource.TICKET_COMMENT, Action.UPDATE, useCallback((data) => {
        if (data?.ticket_id === id) {
            setComments(prev => prev.map(c => c.id === data.id ? { ...c, ...data } : c));
        }
        if (data?.id) highlightRow(data.id, 'UPDATE');
    }, [id, highlightRow]));

    // Handle real-time comment deletions
    useSocketEvent(Resource.TICKET_COMMENT, Action.DELETE, useCallback((data) => {
        if (commentsRef.current.some(c => c.id === data.id)) {
            fetchComments(Math.max(5, commentPage));
        }
    }, [commentPage, fetchComments]));

    const handleStatusChange = async (newStatus) => {
        try {
            setStatusChanging(true);
            setLoading(true);
            const payload = await updateTicket(id, { status: newStatus });
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            setTicket(prev => ({ ...prev, status: newStatus }));
        } catch (error) {
            handleError(error);
        } finally {
            setStatusChanging(false);
            setLoading(false);
        }
    };

    const handleResolveSubmit = async () => {
        if (!resolveText.trim()) return;
        setResolving(true);
        try {
            await resolveTicket(id, { content: resolveText.trim() });
            message.success(t('escalation_resolved'));
            setResolveText('');
            resetResolveBeautifyControl();
            fetchTicket();
        } catch (error) {
            handleError(error);
        } finally {
            setResolving(false);
        }
    };

    // ─── Resizable divider handlers ───
    const MIN_SIDEBAR = 280;
    const MIN_CONVERSATION = 380;

    const handleResizeStart = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            if (!containerRef.current) return;
            const containerRect = containerRef.current.getBoundingClientRect();
            const newWidth = e.clientX - containerRect.left;
            const maxWidth = containerRect.width - MIN_CONVERSATION;
            setSidebarWidth(Math.max(MIN_SIDEBAR, Math.min(newWidth, maxWidth)));
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    const handleDelete = async () => {
        try {
            setLoading(true);
            const payload = await deleteTicket(id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            onClose();
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        try {
            setSubmittingComment(true);
            await createTicketComment(id, { content: newComment.trim() });
            setNewComment('');
            fetchComments(commentPage);
        } catch (error) {
            handleError(error);
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await deleteTicketComment(commentId);
            fetchComments(commentPage);
        } catch (error) {
            handleError(error);
        }
    };

    const displayId = (ticketId) => {
        if (!ticketId) return '';
        return `#${String(ticketId)}`;
    };

    // ─── Loading skeleton ───
    if (loading) {
        return (
            <ResponsiveModal
                open={!!ticketId}
                onCancel={onClose}
                desktopWidth={1200}
                title={t('loading')}
                footer={null}
                styles={{ body: { padding: 0, overflow: 'hidden' } }}
            >
                <div className="ticket-detail-layout" style={{ height: '70vh' }}>
                    <div style={{ width: 340, padding: 20 }}>
                        <Skeleton active paragraph={{ rows: 8 }} />
                    </div>
                    <div style={{ flex: 1, padding: 20 }}>
                        <Skeleton active paragraph={{ rows: 10 }} />
                    </div>
                </div>
            </ResponsiveModal>
        );
    }

    // ─── Not found ───
    if (!ticket) {
        return (
            <ResponsiveModal
                open={!!ticketId}
                onCancel={onClose}
                desktopWidth={600}
                title={t('ticket_not_found')}
                footer={null}
            >
                <Empty description={t('ticket_not_found')} />
                <Flex justify="center" style={{ marginTop: 16 }}>
                    <Button onClick={onClose}>{t('close')}</Button>
                </Flex>
            </ResponsiveModal>
        );
    }

    const isCompact = !screens.md;

    const targetMessage = ticket?.source_message || contextMessages.find(m => m.id === ticket?.source_message_id);

    return (
        <ResponsiveModal
            open={!!ticketId}
            onCancel={onClose}
            desktopWidth={1200}
            title={`${t('ticket_details')} ${displayId(ticket.id)}`}
            footer={null}
            destroyOnClose
            styles={{ body: { padding: 0, overflow: 'hidden' } }}
        >
            <div
                ref={containerRef}
                className={`ticket-detail-layout${isCompact ? ' mobile' : ''}${isResizing ? ' resizing' : ''}`}
            >
                {/* Mobile tab switcher */}
                {isCompact && (
                    <div style={{ padding: '12px 12px 0', flexShrink: 0 }}>
                        <Segmented
                            className="ticket-detail-mobile-tabs"
                            value={mobileTab}
                            onChange={setMobileTab}
                            block
                            options={[
                                { label: <div style={{ whiteSpace: 'normal', lineHeight: 1.2, padding: '8px 0' }}>{t('details', 'Details')}</div>, value: 'info' },
                                { label: <div style={{ whiteSpace: 'normal', lineHeight: 1.2, padding: '8px 0' }}>{t('message_context', 'Conversation')}</div>, value: 'conversation' },
                            ]}
                        />
                    </div>
                )}

                {/* ═══ LEFT SIDEBAR ═══ */}
                <div
                    className="ticket-detail-sidebar"
                    style={{
                        width: isCompact ? 'auto' : sidebarWidth,
                        borderRight: isCompact ? 'none' : `1px solid ${token.colorBorderSecondary}`,
                        display: isCompact && mobileTab !== 'info' ? 'none' : undefined,
                    }}
                >
                    {/* Actions — compact inline bar */}
                    <Flex gap={8} align="center" style={{
                        padding: '10px 14px',
                        borderRadius: 10,
                        background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                        border: `1px solid ${token.colorBorderSecondary}`,
                    }}>
                        <Select
                            value={ticket.status}
                            onChange={handleStatusChange}
                            loading={statusChanging}
                            style={{ flex: 1, minWidth: 0 }}
                            size="middle"
                            options={['open', 'in_progress', 'resolved', 'closed'].map(s => ({
                                label: (
                                    <Space size={6}>
                                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: STATUS_DOT_COLORS[s] }} />
                                        {t(`ticket_status_${s}`)}
                                    </Space>
                                ),
                                value: s,
                            }))}
                        />
                        <Popconfirm title={t('delete_ticket_confirmation')} onConfirm={handleDelete} okText={t('delete')} cancelText={t('cancel')} okType="danger">
                            <Button danger icon={<DeleteOutlined />} size="middle" style={{ flexShrink: 0 }} />
                        </Popconfirm>
                    </Flex>

                    {/* Subject & Description */}
                    <Card styles={{ body: { padding: '16px 18px' } }} style={{ borderRadius: 10 }}>
                        <Title level={5} style={{ margin: '0 0 8px 0', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
                            {ticket.subject || t('no_subject')}
                        </Title>
                        {ticket.description && ticket.description !== ticket.subject ? (
                            <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0, fontSize: 13, lineHeight: 1.7, wordBreak: 'break-word' }}>{ticket.description}</Paragraph>
                        ) : !ticket.description ? (
                            <Text type="secondary" italic>{t('no_description')}</Text>
                        ) : null}
                    </Card>

                    {/* Contact */}
                    {ticket.contact && (
                        <Card styles={{ body: { padding: '14px 18px' } }} style={{ borderRadius: 10 }}
                            title={<Text style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('contact')}</Text>}
                        >
                            <Flex gap={10} align="center">
                                {ticket.contact.picture_url ? (
                                    <>
                                        <Button
                                            type="text"
                                            aria-label={t('view_contact_photo', 'View contact photo')}
                                            onClick={() => setIsContactPhotoPreviewOpen(true)}
                                            style={{ padding: 0, height: 'auto', flexShrink: 0 }}
                                        >
                                            <Avatar size={36} src={ticket.contact.picture_url} />
                                        </Button>
                                        <Image
                                            src={ticket.contact.picture_url}
                                            alt={ticket.contact.full_name || t('contact')}
                                            preview={{ visible: isContactPhotoPreviewOpen, onVisibleChange: setIsContactPhotoPreviewOpen }}
                                            style={{ display: 'none' }}
                                        />
                                    </>
                                ) : (
                                    <Avatar size={36} icon={<UserOutlined />}
                                        style={{ background: isDarkMode ? '#264f78' : '#e6f4ff', color: isDarkMode ? '#69b1ff' : '#1677ff', flexShrink: 0 }}
                                    />
                                )}
                                <Flex vertical style={{ minWidth: 0 }}>
                                    <Text strong style={{ fontSize: 13 }}>{ticket.contact.full_name}</Text>
                                    <Text type="secondary" style={{ fontSize: 11 }}>{ticket.contact.contact_type}</Text>
                                    {ticket.contact.email && <Text type="secondary" style={{ fontSize: 11 }}>{ticket.contact.email}</Text>}
                                </Flex>
                            </Flex>
                        </Card>
                    )}

                    {/* Details */}
                    <Card styles={{ body: { padding: '14px 18px' } }} style={{ borderRadius: 10 }}
                        title={<Text style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('ticket_details')}</Text>}
                    >
                        <Flex vertical gap={12}>
                            <MetaRow icon={<FlagOutlined />} label={t('ticket_status')}>
                                <Tag color={STATUS_COLORS[ticket.status]} style={{ borderRadius: 4, margin: 0 }}>{t(`ticket_status_${ticket.status}`)}</Tag>
                            </MetaRow>
                            <MetaRow icon={<TagOutlined />} label={t('ticket_priority')}>
                                <Tag color={PRIORITY_COLORS[ticket.priority]} style={{ borderRadius: 4, margin: 0 }}>{t(`ticket_priority_${ticket.priority}`)}</Tag>
                            </MetaRow>
                            {ticket.category && (
                                <MetaRow icon={<AppstoreOutlined />} label={t('ticket_category')}>
                                    <Tag color={CATEGORY_COLORS[ticket.category]} style={{ borderRadius: 4, margin: 0 }}>{t(`ticket_category_${ticket.category}`)}</Tag>
                                </MetaRow>
                            )}
                            {ticket.channel && (
                                <MetaRow icon={<MessageOutlined />} label={t('channel')}>
                                    <Flex align="center" gap={8}>
                                        <Avatar src={ticket.channel.picture_url} size={20} icon={<AppstoreOutlined />} />
                                        <Flex vertical>
                                            <Text strong style={{ fontSize: 13, lineHeight: 1.2 }}>{ticket.channel.name}</Text>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {{ 'whatsapp': t('whatsapp'), 'messenger': t('messenger'), 'facebook_page': t('facebook'), 'instagram': t('instagram') }[ticket.channel.channel_type] || ticket.channel.channel_type}
                                            </Text>
                                        </Flex>
                                    </Flex>
                                </MetaRow>
                            )}
                            {ticket.agent && (
                                <MetaRow icon={<RobotOutlined />} label={t('agent')}>
                                    <Flex align="center" gap={8}>
                                        <Avatar size={20} icon={<RobotOutlined />} style={{ background: isDarkMode ? '#00474f' : '#e6fffb', color: isDarkMode ? '#36cfc9' : '#13c2c2' }} />
                                        <Text strong style={{ fontSize: 13 }}>{ticket.agent.name}</Text>
                                    </Flex>
                                </MetaRow>
                            )}
                            <MetaRow icon={ticket.opened_by_type === 'agent' ? <RobotOutlined /> : <UserOutlined />} label={t('opened_by_type')}>
                                <Text style={{ fontSize: 13 }}>{t(`opened_by_${ticket.opened_by_type}`) || ticket.opened_by_type}</Text>
                            </MetaRow>
                            <Divider style={{ margin: '4px 0' }} />
                            <MetaRow icon={<CalendarOutlined />} label={t('created_at')}>
                                <Text style={{ fontSize: 12 }}>{formatTicketDate(ticket.created_at)}</Text>
                            </MetaRow>
                            {ticket.resolved_at && (
                                <MetaRow icon={<CheckCircleOutlined />} label={t('resolved_at')}>
                                    <Text style={{ fontSize: 12 }}>{formatTicketDate(ticket.resolved_at)}</Text>
                                </MetaRow>
                            )}
                            {ticket.closed_at && (
                                <MetaRow icon={<LockOutlined />} label={t('closed_at')}>
                                    <Text style={{ fontSize: 12 }}>{formatTicketDate(ticket.closed_at)}</Text>
                                </MetaRow>
                            )}
                            {ticket.resolved_by && (
                                <MetaRow icon={<UserOutlined />} label={t('resolved_by')}>
                                    <Text style={{ fontSize: 12 }}>{ticket.resolved_by.full_name}</Text>
                                </MetaRow>
                            )}
                            {ticket.admin_response?.content?.trim() && (
                                <Card type="inner" size="small" title={t('admin_response')}>
                                    <Flex vertical gap={4}>
                                        <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' }}>
                                            {ticket.admin_response.content}
                                        </Text>
                                        {ticket.admin_response.sent_at && (
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {formatTicketDate(ticket.admin_response.sent_at)}
                                            </Text>
                                        )}
                                    </Flex>
                                </Card>
                            )}
                            {ticket.agent_response?.content?.trim() && (
                                <Card type="inner" size="small" title={t('agent_response')}>
                                    <Flex vertical gap={4}>
                                        <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' }}>
                                            {ticket.agent_response.content}
                                        </Text>
                                        {ticket.agent_response.sent_at && (
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {formatTicketDate(ticket.agent_response.sent_at)}
                                            </Text>
                                        )}
                                    </Flex>
                                </Card>
                            )}
                        </Flex>
                    </Card>

                    {/* Comments */}
                    <Card styles={{ body: { padding: '14px 18px' } }} style={{ borderRadius: 10 }}
                        title={
                            <Flex align="center" gap={8}>
                                <CommentOutlined />
                                <span>{t('comments')}</span>
                                {(ticket?.comment_count || commentsMeta?.total) > 0 && (
                                    <Tag style={{ borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{ticket?.comment_count || commentsMeta?.total}</Tag>
                                )}
                            </Flex>
                        }
                    >
                        {commentsLoading ? (
                            <Skeleton active paragraph={{ rows: 3 }} />
                        ) : comments.length === 0 ? (
                            <Empty description={t('no_comments')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '12px 0' }} />
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {comments.map((comment) => (
                                    <div key={comment.id} className={getHighlightClass(comment.id, 'socket-highlight-target')}
                                        style={{ padding: '10px 12px', borderRadius: 8, transition: 'background 0.15s' }}
                                        onMouseEnter={e => !getHighlightClass(comment.id) && (e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)')}
                                        onMouseLeave={e => !getHighlightClass(comment.id) && (e.currentTarget.style.background = 'transparent')}
                                    >
                                        <Flex gap={10} align="flex-start">
                                            <Avatar size={28} icon={<UserOutlined />}
                                                style={{ flexShrink: 0, background: isDarkMode ? '#264f78' : '#e6f4ff', color: isDarkMode ? '#69b1ff' : '#1677ff' }}
                                            />
                                            <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                                                <Text type="secondary" style={{ fontSize: 11, marginBottom: 2 }}>{formatFullTime(comment.created_at) || comment.created_at}</Text>
                                                <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' }}>{comment.content}</Text>
                                            </Flex>
                                            <Popconfirm title={t('delete_comment_confirmation')} onConfirm={() => handleDeleteComment(comment.id)} okText={t('delete')} cancelText={t('cancel')} okType="danger">
                                                <Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ opacity: 0.4, flexShrink: 0 }} />
                                            </Popconfirm>
                                        </Flex>
                                    </div>
                                ))}
                            </div>
                        )}
                        {commentsMeta && commentsMeta.total > commentsMeta.page_size && (
                            <Flex justify="center" style={{ marginTop: 12 }}>
                                <Pagination current={commentsMeta.page} pageSize={commentsMeta.page_size} total={commentsMeta.total}
                                    onChange={(page) => { setCommentPage(page); fetchComments(page); }} size="small"
                                />
                            </Flex>
                        )}
                        <div style={{ padding: 10, borderRadius: 8, background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)', marginTop: 12 }}>
                            <TextArea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder={t('add_comment_placeholder')}
                                rows={2} maxLength={2000} variant="borderless" style={{ fontSize: 13, resize: 'none', unicodeBidi: 'plaintext' }}
                                dir="auto"
                                onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                            />
                            <Flex justify="flex-end" style={{ marginTop: 6 }}>
                                <Button type="primary" icon={<SendOutlined />} size="small" loading={submittingComment} onClick={handleAddComment} disabled={!newComment.trim()}>
                                    {t('send')}
                                </Button>
                            </Flex>
                        </div>
                    </Card>
                </div>

                {/* ═══ RESIZABLE DIVIDER ═══ */}
                {!isCompact && (
                    <div
                        className={`ticket-detail-divider${isDarkMode ? ' dark' : ''}${isResizing ? ' active' : ''}`}
                        onMouseDown={handleResizeStart}
                    />
                )}

                {/* ═══ RIGHT CONVERSATION ═══ */}
                <div className="ticket-detail-conversation" style={{
                    display: isCompact && mobileTab !== 'conversation' ? 'none' : undefined,
                }}>
                    <div className="ticket-detail-thread" ref={messageListRef}>
                        {ticket.source_message_id ? (
                            loadingContext ? (
                                <Skeleton active paragraph={{ rows: 6 }} />
                            ) : contextMessages.length === 0 ? (
                                <Empty description={t('no_messages')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                <>
                                    {contextMessages.map((msg) => {
                                        const isTarget = msg.id === ticket.source_message_id;
                                        return (
                                            <div key={msg.id} ref={isTarget ? targetMessageRef : null}
                                                style={{
                                                    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                                    position: 'relative',
                                                    padding: isTarget ? '12px' : '0 12px',
                                                    margin: isTarget ? '4px 0' : undefined,
                                                    borderRadius: isTarget ? '12px' : undefined,
                                                    background: isTarget ? (isDarkMode ? `${token.colorPrimary}15` : `${token.colorPrimary}0A`) : undefined,
                                                    border: isTarget ? `1px solid ${token.colorPrimary}40` : undefined,
                                                    boxShadow: isTarget ? `0 4px 16px -4px ${token.colorPrimary}30` : undefined,
                                                }}
                                            >
                                                {isTarget && (
                                                    <div style={{ position: 'absolute', insetInlineStart: 0, top: '15%', bottom: '15%', width: 4, backgroundColor: token.colorPrimary, borderRadius: 4 }} />
                                                )}
                                                <MessageBubble message={msg} screens={screens} messageRefs={messageRefs} isViewOnly={true} />
                                            </div>
                                        );
                                    })}
                                    <Button type="link" size="small" icon={<LinkOutlined />}
                                        onClick={(e) => { e.stopPropagation(); onClose(); navigate(`/messages?contactId=${ticket.contact_id}`); }}
                                        style={{ paddingInlineStart: 0, marginTop: 8, fontSize: 12 }}
                                    >
                                        {t('view_full_conversation')}
                                    </Button>
                                </>
                            )
                        ) : (
                            <Flex align="center" justify="center" style={{ height: '100%', opacity: 0.5 }}>
                                <Empty description={t('no_source_message')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            </Flex>
                        )}
                    </div>

                    {/* Resolve reply bar — only for unresolved tickets */}
                    {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                        <div className="ticket-detail-reply-bar" style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, background: isDarkMode ? '#1f1f1f' : '#fafafa' }}>
                            <TextArea value={resolveText} onChange={(e) => setResolveText(e.target.value)}
                                placeholder={t('type_your_response')} rows={2} maxLength={4000}
                                style={{ fontSize: 13, resize: 'none', borderRadius: 8, unicodeBidi: 'plaintext' }}
                                dir="auto"
                                onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); handleResolveSubmit(); } }}
                            />
                            <Flex justify="space-between" align="center">
                                <BeautifyControl
                                    value={resolveText} onChange={setResolveText}
                                    runBeautify={async (text) => {
                                        const response = await suggestTicketResponse(id, { content: text });
                                        return { suggestions: response?.data?.suggestions || [], analysis: response?.data?.analysis || '', coinsAmount: response?.data?.coins_amount };
                                    }}
                                    beautifyLabel={t('beautify')} revertLabel={t('revert')}
                                    beautifyIcon={<HighlightOutlined />} buttonStyle={{ color: '#7F56D9' }} allowEmpty
                                    resetSignal={resolveBeautifyResetSignal}
                                />
                                <Button type="primary" icon={<CheckCircleOutlined />} loading={resolving}
                                    onClick={handleResolveSubmit} disabled={!resolveText.trim()}
                                >
                                    {t('resolve_escalation')}
                                </Button>
                            </Flex>
                        </div>
                    )}
                </div>
            </div>
        </ResponsiveModal>
    );
};

export default TicketDetail;
